/**
 * useAnchorRect — B-2026-0088 Phase A3
 *
 * Tracks the `getBoundingClientRect()` of the anchor element for the active
 * guide key via a `requestAnimationFrame` loop. Updates React state only when
 * the rect changes by more than 1px (epsilon guard) to prevent unnecessary
 * re-renders when the element is stationary.
 *
 * Outputs:
 *   rect         — the current DOMRect (or null before first rAF fires)
 *   elementFound — whether the anchor element exists in the DOM
 *   isOffscreen  — whether the element's rect is outside the visible viewport
 *   isCovered    — (desktop only) whether the rect is behind the 320px right drawer
 *
 * Lifecycle:
 *   - Starts the rAF loop when `active` is true.
 *   - Cancels the loop when `active` becomes false or on unmount.
 *   - Initial rect is null so AnchorHighlight renders nothing until rAF fires.
 *
 * Test design:
 *   Tests use `mockRequestAnimationFrame()` from testUtils — the `triggerNextFrame()`
 *   helper fires the pending callback synchronously.
 */

import { useState, useEffect, useRef } from 'react';
import { resolveAnchorElement } from '@/data/guideAnchorRegistry';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AnchorRectState {
  rect: DOMRect | null;
  elementFound: boolean;
  isOffscreen: boolean;
  /** True when the element rect is behind the 320px desktop drawer. */
  isCovered: boolean;
}

const DRAWER_WIDTH = 320;
const EPSILON = 1; // px — ignore sub-pixel jitter

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function rectsEqualWithEpsilon(a: DOMRect, b: DOMRect): boolean {
  return (
    Math.abs(a.top - b.top) < EPSILON &&
    Math.abs(a.left - b.left) < EPSILON &&
    Math.abs(a.width - b.width) < EPSILON &&
    Math.abs(a.height - b.height) < EPSILON
  );
}

function isRectOffscreen(rect: DOMRect): boolean {
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return (
    rect.bottom < 0 ||
    rect.top > vh ||
    rect.right < 0 ||
    rect.left > vw
  );
}

function isRectCovered(rect: DOMRect): boolean {
  // The desktop drawer is fixed to the right edge at 320px wide.
  // An element is "covered" if its right edge is past (vw - DRAWER_WIDTH).
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return rect.right > vw - DRAWER_WIDTH;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAnchorRect(
  guideKey: string | null,
  active: boolean,
): AnchorRectState {
  const [state, setState] = useState<AnchorRectState>({
    rect: null,
    elementFound: false,
    isOffscreen: false,
    isCovered: false,
  });

  const rafIdRef = useRef<number | null>(null);
  const lastRectRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    if (!active || !guideKey) {
      // Clean up and reset.
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      setState({ rect: null, elementFound: false, isOffscreen: false, isCovered: false });
      lastRectRef.current = null;
      return;
    }

    function tick() {
      const el = resolveAnchorElement(guideKey!);

      if (!el) {
        // Element not found — update state if we previously had one.
        if (lastRectRef.current !== null || state.elementFound) {
          setState({ rect: null, elementFound: false, isOffscreen: false, isCovered: false });
          lastRectRef.current = null;
        }
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const newRect = (el as HTMLElement).getBoundingClientRect();
      const offscreen = isRectOffscreen(newRect);
      const covered = isRectCovered(newRect);

      // Only update state if rect changed beyond epsilon.
      if (
        lastRectRef.current === null ||
        !rectsEqualWithEpsilon(lastRectRef.current, newRect)
      ) {
        lastRectRef.current = newRect;
        setState({
          rect: newRect,
          elementFound: true,
          isOffscreen: offscreen,
          isCovered: covered,
        });
      } else {
        // Rect unchanged but offscreen/covered might have changed.
        setState((prev) => {
          if (prev.isOffscreen !== offscreen || prev.isCovered !== covered || !prev.elementFound) {
            return { rect: newRect, elementFound: true, isOffscreen: offscreen, isCovered: covered };
          }
          return prev;
        });
      }

      rafIdRef.current = requestAnimationFrame(tick);
    }

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  // We intentionally omit `state` from deps to avoid the re-subscribe loop;
  // `setState` is stable. `active` and `guideKey` are the real triggers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, guideKey]);

  return state;
}
